import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Form } from 'react-bootstrap';
import ClientInfoForm from '../ClientInfoForm';
import AddressDropdown from '../AddressDropdown';
import AddAddressModal from '../AddAddressModal';
import { updateClient, updateOrder, getClientById, logError } from '../../api';

const EditClientInOrder = ({ order, onSaved, onCancel }) => {
    const [client, setClient] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [selectedBilling, setSelectedBilling] = useState(null);
    const [billingSame, setBillingSame] = useState(true);
    const [editingAddress, setEditingAddress] = useState(null); // 'shipping' | 'new-shipping' | 'billing' | 'new-billing' | null
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const fullClient = await getClientById(order.clientId);
                const addrs = fullClient.addresses || [];
                setClient(fullClient);
                setAddresses(addrs);

                const shipping = addrs.find(a => a.id === order.addressId) || addrs.find(a => a.isDefault) || addrs[0] || null;
                const billing = addrs.find(a => a.id === order.billingAddressId) || shipping;
                const same = !order.billingAddressId || order.addressId === order.billingAddressId;

                setSelectedShipping(shipping);
                setSelectedBilling(billing);
                setBillingSame(same);
            } catch (err) {
                logError(err);
                alert('Error al cargar los datos del cliente.');
                onCancel();
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const handleShippingSelect = (addr) => {
        setSelectedShipping(addr);
        if (billingSame) setSelectedBilling(addr);
        setIsDirty(true);
    };

    const handleBillingSelect = (addr) => {
        setSelectedBilling(addr);
        setIsDirty(true);
    };

    const handleBillingSameToggle = (e) => {
        const same = e.target.checked;
        setBillingSame(same);
        if (same) setSelectedBilling(selectedShipping);
        setIsDirty(true);
    };

    const handleAddressSaved = (savedAddress) => {
        setAddresses(prev => {
            const exists = prev.find(a => a.id === savedAddress.id);
            return exists
                ? prev.map(a => a.id === savedAddress.id ? savedAddress : a)
                : [...prev, savedAddress];
        });

        const isShipping = editingAddress === 'shipping' || editingAddress === 'new-shipping';
        if (isShipping) {
            setSelectedShipping(savedAddress);
            if (billingSame) setSelectedBilling(savedAddress);
        } else {
            setSelectedBilling(savedAddress);
        }

        setEditingAddress(null);
        setIsDirty(true);
    };

    const handleClientSubmit = async (clientData) => {
        setIsSaving(true);
        try {
            await updateClient(clientData.id, clientData);
            const effectiveBilling = billingSame ? selectedShipping : selectedBilling;
            await updateOrder(order.id, {
                id: order.id,
                addressId: selectedShipping?.id,
                billingAddressId: effectiveBilling?.id,
            });
            onSaved();
        } catch (error) {
            logError(error);
            alert('Error al guardar los cambios.');
        } finally {
            setIsSaving(false);
        }
    };

    const addressForModal = (editingAddress === 'new-shipping' || editingAddress === 'new-billing')
        ? null
        : editingAddress === 'shipping' ? selectedShipping : selectedBilling;

    return (
        <>
            <Modal show onHide={onCancel} size="lg" centered scrollable>
                <Modal.Header closeButton={!isSaving}>
                    <Modal.Title>Editar Cliente/Dirección</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <h5 className="text-muted border-bottom pb-2">Direcciones del Pedido</h5>

                                <Form.Label className="fw-semibold">Dirección de envío</Form.Label>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <AddressDropdown
                                        addresses={addresses}
                                        selectedAddress={selectedShipping}
                                        onSelectAddress={handleShippingSelect}
                                        id="edit-shipping-address"
                                    />
                                    <Button variant="outline-secondary" size="sm" onClick={() => setEditingAddress('shipping')}>
                                        Editar
                                    </Button>
                                    <Button variant="outline-primary" size="sm" onClick={() => setEditingAddress('new-shipping')}>
                                        Nueva
                                    </Button>
                                </div>

                                <Form.Check
                                    type="checkbox"
                                    label="Usar la misma dirección para facturación"
                                    checked={billingSame}
                                    onChange={handleBillingSameToggle}
                                    className="mb-3"
                                />

                                {!billingSame && (
                                    <>
                                        <Form.Label className="fw-semibold">Dirección de facturación</Form.Label>
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <AddressDropdown
                                                addresses={addresses}
                                                selectedAddress={selectedBilling}
                                                onSelectAddress={handleBillingSelect}
                                                id="edit-billing-address"
                                            />
                                            <Button variant="outline-secondary" size="sm" onClick={() => setEditingAddress('billing')}>
                                                Editar
                                            </Button>
                                            <Button variant="outline-primary" size="sm" onClick={() => setEditingAddress('new-billing')}>
                                                Nueva
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>

                            <ClientInfoForm
                                client={client}
                                isEditing={true}
                                handleSubmit={handleClientSubmit}
                                handleCancel={onCancel}
                                setIsDirty={setIsDirty}
                                showCancelButton={true}
                                hideAddress={true}
                            />

                            {isSaving && (
                                <div className="text-center mt-2">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Guardando...
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
            </Modal>

            {editingAddress && (
                <AddAddressModal
                    show
                    handleClose={() => setEditingAddress(null)}
                    handleSave={handleAddressSaved}
                    address={addressForModal}
                    clientId={client?.id}
                />
            )}
        </>
    );
};

export default EditClientInOrder;
